/**
 * VersionTable — promoted catalog versions with a restore action. Demo page: /tables/basic-tables.
 */
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { Link } from 'react-router-dom';
import { ChevronRight, GitBranch } from 'lucide-react';
import { Badge, Button, EmptyState } from '../../../components/ui/index.js';
import { Card } from '../../../components/tailgrids/core/card.js';
import type { CatalogItemVersion } from './catalog-types.js';
import { libraryItemPath } from './catalog-helpers.js';

export function VersionTable({
  versions,
  onRestore,
  restoringVersionId,
}: {
  versions: CatalogItemVersion[];
  onRestore: (version: CatalogItemVersion) => void;
  restoringVersionId?: number | null;
}) {
  if (versions.length === 0)
    return (
      <EmptyState
        icon={GitBranch}
        title="No promoted versions"
        description="Promoted library versions will appear here."
      />
    );
  return (
    <Card className="divide-y overflow-hidden p-0">
      {versions.map((version) => {
        const warnings = Array.isArray(version.metadata?.warnings) ? version.metadata.warnings.map(String) : [];
        const targetType = String(version.metadata?.materializedTargetType ?? version.targetType);
        const targetId = Number(version.metadata?.materializedTargetId ?? version.targetId);
        const targetLabel = String(version.metadata?.materializedTargetLabel ?? '');
        const itemPath = libraryItemPath(targetType, targetId, targetLabel);
        return (
          <div key={version.id} className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-heading">
                {String(version.metadata?.materializedTargetLabel ?? `${version.targetType} #${version.targetId}`)}
              </h2>
              <Badge variant="success">v{version.version}</Badge>
              <Badge variant="neutral">{sentenceCase(version.targetType)}</Badge>
              {version.adoption?.newerVersionAvailable && <Badge variant="warning">update available</Badge>}
              {version.status && version.status !== 'active' && <Badge variant="info">{version.status}</Badge>}
            </div>
            <p className="text-sm text-muted mt-2">{version.diffSummary ?? 'No version summary supplied.'}</p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs text-muted">
              <span>Catalog version: {version.id}</span>
              <span>Source: {version.sourceId}</span>
              <span>Candidate: {version.candidateId ?? 'n/a'}</span>
              <span>Target: #{String(version.metadata?.materializedTargetId ?? version.targetId)}</span>
              <span>Promoted: {new Date(version.promotedAt).toLocaleDateString()}</span>
              <span>Hash: {version.versionHash.slice(0, 12)}</span>
              <span>Promoted by: {version.promotedBy ?? 'system'}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {itemPath && (
                <Link to={itemPath} className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline">
                  Open promoted library item <ChevronRight size={13} />
                </Link>
              )}
              {version.adoption?.newerVersionAvailable && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={restoringVersionId === version.id}
                  onClick={() => onRestore(version)}
                >
                  Restore this version
                </Button>
              )}
            </div>
            {warnings.length > 0 && <div className="text-xs text-muted mt-2">{warnings.join(' ')}</div>}
          </div>
        );
      })}
    </Card>
  );
}
